import { useUser } from "@clerk/clerk-react";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { isRole, type Role } from "../lib/roles";

interface RoleContextValue {
  role: Role | null;
  /** True until the initial role lookup (Clerk) has settled. */
  loading: boolean;
}

const RoleContext = createContext<RoleContextValue>({
  role: null,
  loading: true,
});

export function RoleProvider({ children }: { children: ReactNode }) {
  const { user, isLoaded } = useUser();

  const [role, setRole] = useState<Role | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoaded) return;

    if (user) {
      const rawRole = user.publicMetadata?.role;

      if (typeof rawRole === "string" && isRole(rawRole)) {
        setRole(rawRole);
      } else {
        setRole(null);
      }
    } else {
      setRole(null);
    }

    setLoading(false);
  }, [user, isLoaded]);

  return <RoleContext.Provider value={{ role, loading }}>{children}</RoleContext.Provider>;
}

/** Returns the current user's role. */
export function useRole(): RoleContextValue {
  return useContext(RoleContext);
}
